import React, { useEffect, useRef } from "react";
import * as THREE from "three";

const Home3D = () => {
  const containerRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    // ---- environment / device detection (same behavior as original) ----
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isLowPowerDevice = isMobile || (navigator.hardwareConcurrency || 1) <= 4;
    const devicePixelRatio = Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2);

    // ---- three.js objects ----
    let scene, camera, renderer, material, mesh, clock;
    let frameCount = 0;
    let lastTime = performance.now();
    let fps = 0;
    let mousePosition = new THREE.Vector2(0.5, 0.5);
    let targetMousePosition = new THREE.Vector2(0.5, 0.5);
    let cursorSphere3D = new THREE.Vector3(0, 0, 0);
    let activeMerges = 0;

    // ---- settings & presets (copied from your JS) ----
    const presets = {
      holographic: {
        sphereCount: isMobile ? 4 : 6,
        ambientIntensity: 0.12,
        diffuseIntensity: 1.2,
        specularIntensity: 2.5,
        specularPower: 3,
        fresnelPower: 0.8,
        backgroundColor: new THREE.Color(0x111111), // dark gray background
        sphereColor: new THREE.Color(0x333333),     // medium-dark gray sphere
        lightColor: new THREE.Color(0xbbbbbb),      // soft light gray highlight        
        lightPosition: new THREE.Vector3(0.9, 0.9, 1.2),
        smoothness: 0.8,
        contrast: 1.6,
        fogDensity: 0.06,
        cursorGlowIntensity: 1.2,
        cursorGlowRadius: 2.2,
        cursorGlowColor: new THREE.Color(0xd2ff1f)
      }
      // (You had many presets — include others if you want to support them)
    };

    const settings = {
      preset: "holographic",
      ...presets.holographic,
      fixedTopLeftRadius: 0.8,
      fixedBottomRightRadius: 0.9,
      smallTopLeftRadius: 0.3,
      smallBottomRightRadius: 0.35,
      cursorRadiusMin: 0.08,
      cursorRadiusMax: 0.15,
      animationSpeed: 0.6,
      movementScale: 1.2,
      mouseSmoothness: 0.1,
      mergeDistance: 1.5,
      mouseProximityEffect: true,
      minMovementScale: 0.3,
      maxMovementScale: 1.0
    };

    // ---- helper functions used in shader & JS ----
    function screenToWorldJS(normalizedX, normalizedY) {
      const uv_x = normalizedX * 2.0 - 1.0;
      const uv_y = normalizedY * 2.0 - 1.0;
      const aspect = window.innerWidth / window.innerHeight;
      // matches your original JS mapping
      return new THREE.Vector3(uv_x * aspect * 2.0, uv_y * 2.0, 0.0);
    }

    function getStoryText(x, y, radius, merges, framesPerSec) {
      if (isMobile) {
        return `vessel: (${x}, ${y})\nfield: ${radius}u\nmerges: ${merges}\nflux: ${framesPerSec}hz`;
      }
      return `our vessel drifts at coordinates (${x}, ${y})\ngravitational field extends ${radius} units into quantum foam\ncurrently merging with ${merges} other entities\ntemporal flux: ${framesPerSec} cycles per second`;
    }

    function updateStory(x, y, radius, merges, fpsValue) {
      const el = document.getElementById("story-text");
      if (!el) return;
      const newText = getStoryText(x.toFixed(2), y.toFixed(2), radius.toFixed(2), merges, fpsValue || 0);
      // innerHTML used in original; using safe plain text (line breaks)
      el.innerText = newText;
    }

    // ---- build scene ----
    function init() {
      const container = containerRef.current;
      if (!container) return;

      scene = new THREE.Scene();
      camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
      camera.position.z = 1;

      clock = new THREE.Clock();

      renderer = new THREE.WebGLRenderer({
        antialias: !isMobile && !isLowPowerDevice,
        alpha: true,
        powerPreference: isMobile ? "default" : "high-performance",
        preserveDrawingBuffer: false,
        premultipliedAlpha: false
      });



      // pixel ratio and size
      const pixelRatio = Math.min(devicePixelRatio, isMobile ? 1.5 : 2);
      renderer.setPixelRatio(pixelRatio);
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setClearColor(0x000000, 0);
      // output color space if available
      if (renderer.outputColorSpace !== undefined) {
        try {
          renderer.outputColorSpace = THREE.SRGBColorSpace;
        } catch (e) {
          // older three builds may not support outputColorSpace assignment
        }
      }

      // style canvas as full-screen fixed
      const canvas = renderer.domElement;
      canvas.style.position = "relative";
      canvas.style.top = "0";
      canvas.style.left = "0";
      canvas.style.width = "100vw";
      canvas.style.height = "100vh";
      canvas.style.zIndex = "0";
      canvas.style.display = "block";
      canvas.setAttribute("aria-hidden", "true");

      container.appendChild(canvas);

      // precision selection line (same logic used earlier)
      const precisionLine =
        isMobile || isSafari || isLowPowerDevice ? "precision mediump float;" : "precision highp float;";

      // ---- create full ShaderMaterial (uses your full fragment and vertex shader) ----
      material = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
          uActualResolution: {
            value: new THREE.Vector2(window.innerWidth * pixelRatio, window.innerHeight * pixelRatio)
          },
          uPixelRatio: { value: pixelRatio },
          uMousePosition: { value: new THREE.Vector2(0.5, 0.5) },
          uCursorSphere: { value: new THREE.Vector3(0, 0, 0) },
          uCursorRadius: { value: settings.cursorRadiusMin },
          uSphereCount: { value: settings.sphereCount },
          uFixedTopLeftRadius: { value: settings.fixedTopLeftRadius },
          uFixedBottomRightRadius: { value: settings.fixedBottomRightRadius },
          uSmallTopLeftRadius: { value: settings.smallTopLeftRadius },
          uSmallBottomRightRadius: { value: settings.smallBottomRightRadius },
          uMergeDistance: { value: settings.mergeDistance },
          uSmoothness: { value: settings.smoothness },
          uAmbientIntensity: { value: settings.ambientIntensity },
          uDiffuseIntensity: { value: settings.diffuseIntensity },
          uSpecularIntensity: { value: settings.specularIntensity },
          uSpecularPower: { value: settings.specularPower },
          uFresnelPower: { value: settings.fresnelPower },
          uBackgroundColor: { value: settings.backgroundColor },
          uSphereColor: { value: settings.sphereColor },
          uLightColor: { value: settings.lightColor },
          uLightPosition: { value: settings.lightPosition },
          uContrast: { value: settings.contrast },
          uFogDensity: { value: settings.fogDensity },
          uAnimationSpeed: { value: settings.animationSpeed },
          uMovementScale: { value: settings.movementScale },
          uMouseProximityEffect: { value: settings.mouseProximityEffect ? 1.0 : 0.0 },
          uMinMovementScale: { value: settings.minMovementScale },
          uMaxMovementScale: { value: settings.maxMovementScale },
          uCursorGlowIntensity: { value: settings.cursorGlowIntensity },
          uCursorGlowRadius: { value: settings.cursorGlowRadius },
          uCursorGlowColor: { value: settings.cursorGlowColor },
          uIsSafari: { value: isSafari ? 1.0 : 0.0 },
          uIsMobile: { value: isMobile ? 1.0 : 0.0 },
          uIsLowPower: { value: isLowPowerDevice ? 1.0 : 0.0 }
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader:
          precisionLine +
          `
      uniform float uTime;
      uniform vec2 uResolution;
      uniform vec2 uActualResolution;
      uniform float uPixelRatio;
      uniform vec2 uMousePosition;
      uniform vec3 uCursorSphere;
      uniform float uCursorRadius;
      uniform int uSphereCount;
      uniform float uFixedTopLeftRadius;
      uniform float uFixedBottomRightRadius;
      uniform float uSmallTopLeftRadius;
      uniform float uSmallBottomRightRadius;
      uniform float uMergeDistance;
      uniform float uSmoothness;
      uniform float uAmbientIntensity;
      uniform float uDiffuseIntensity;
      uniform float uSpecularIntensity;
      uniform float uSpecularPower;
      uniform float uFresnelPower;
      uniform vec3 uBackgroundColor;
      uniform vec3 uSphereColor;
      uniform vec3 uLightColor;
      uniform vec3 uLightPosition;
      uniform float uContrast;
      uniform float uFogDensity;
      uniform float uAnimationSpeed;
      uniform float uMovementScale;
      uniform bool uMouseProximityEffect;
      uniform float uMinMovementScale;
      uniform float uMaxMovementScale;
      uniform float uCursorGlowIntensity;
      uniform float uCursorGlowRadius;
      uniform vec3 uCursorGlowColor;
      uniform float uIsSafari;
      uniform float uIsMobile;
      uniform float uIsLowPower;

      varying vec2 vUv;

      const float PI = 3.14159265359;
      const float EPSILON = 0.001;
      const float MAX_DIST = 100.0;

      float smin(float a, float b, float k) {
        float h = max(k - abs(a - b), 0.0) / k;
        return min(a, b) - h * h * k * 0.25;
      }

      float sdSphere(vec3 p, float r) {
        return length(p) - r;
      }

      vec3 screenToWorld(vec2 normalizedPos) {
        vec2 uv = normalizedPos * 2.0 - 1.0;
        uv.x *= uResolution.x / uResolution.y;
        return vec3(uv * 2.0, 0.0);
      }

      float getDistanceToCenter(vec2 pos) {
        float dist = length(pos - vec2(0.5, 0.5)) * 2.0;
        return smoothstep(0.0, 1.0, dist);
      }

      float sceneSDF(vec3 pos) {
        float result = MAX_DIST;

vec3 topLeftPos = screenToWorld(vec2(0.08, 0.85));
float topLeft = sdSphere(pos - topLeftPos, uFixedTopLeftRadius);

vec3 smallTopLeftPos = screenToWorld(vec2(0.25, 0.85));
float smallTopLeft = sdSphere(pos - smallTopLeftPos, uSmallTopLeftRadius);

vec3 bottomRightPos = screenToWorld(vec2(0.72, 0.85));
float bottomRight = sdSphere(pos - bottomRightPos, uFixedBottomRightRadius);

vec3 smallBottomRightPos = screenToWorld(vec2(0.92, 0.85));
float smallBottomRight = sdSphere(pos - smallBottomRightPos, uSmallBottomRightRadius);


        float t = uTime * uAnimationSpeed;

        float dynamicMovementScale = uMovementScale;
        if (uMouseProximityEffect) {
          float distToCenter = getDistanceToCenter(uMousePosition);
          float mixFactor = smoothstep(0.0, 1.0, distToCenter);
          dynamicMovementScale = mix(uMinMovementScale, uMaxMovementScale, mixFactor);
        }

        int maxIter = uIsMobile > 0.5 ? 4 : (uIsLowPower > 0.5 ? 6 : min(uSphereCount, 10));
        for (int i = 0; i < 10; i++) {
          if (i >= uSphereCount || i >= maxIter) break;

          float fi = float(i);
          float speed = 0.4 + fi * 0.12;
          float radius = 0.12 + mod(fi, 3.0) * 0.06;
          float orbitRadius = (0.3 + mod(fi, 3.0) * 0.15) * dynamicMovementScale;
          float phaseOffset = fi * PI * 0.35;

          float distToCursor = length(vec3(0.0) - uCursorSphere);
          float proximityScale = 1.0 + (1.0 - smoothstep(0.0, 1.0, distToCursor)) * 0.5;
          orbitRadius *= proximityScale;

          vec3 offset;
          if (i == 0) {
            offset = vec3(
              sin(t * speed) * orbitRadius * 0.7,
              sin(t * 0.5) * orbitRadius,
              cos(t * speed * 0.7) * orbitRadius * 0.5
            );
          } else if (i == 1) {
            offset = vec3(
              sin(t * speed + PI) * orbitRadius * 0.5,
              -sin(t * 0.5) * orbitRadius,
              cos(t * speed * 0.7 + PI) * orbitRadius * 0.5
            );
          } else {
            offset = vec3(
              sin(t * speed + phaseOffset) * orbitRadius * 0.8,
              cos(t * speed * 0.85 + phaseOffset * 1.3) * orbitRadius * 0.6,
              sin(t * speed * 0.5 + phaseOffset) * 0.3
            );
          }

          vec3 toCursor = uCursorSphere - offset;
          float cursorDist = length(toCursor);
          if (cursorDist < uMergeDistance && cursorDist > 0.0) {
            float attraction = (1.0 - cursorDist / uMergeDistance) * 0.3;
            offset += normalize(toCursor) * attraction;
          }

          float movingSphere = sdSphere(pos - offset, radius);

          float blend = 0.05;
          if (cursorDist < uMergeDistance) {
            float influence = 1.0 - (cursorDist / uMergeDistance);
            blend = mix(0.05, uSmoothness, influence * influence * influence);
          }

          result = smin(result, movingSphere, blend);
        }

        float cursorBall = sdSphere(pos - uCursorSphere, uCursorRadius);

        float topLeftGroup = smin(topLeft, smallTopLeft, 0.4);
        float bottomRightGroup = smin(bottomRight, smallBottomRight, 0.4);

        result = smin(result, topLeftGroup, 0.3);
        result = smin(result, bottomRightGroup, 0.3);
        result = smin(result, cursorBall, uSmoothness);

        return result;
      }

      vec3 calcNormal(vec3 p) {
        float eps = uIsLowPower > 0.5 ? 0.002 : 0.001;
        return normalize(vec3(
          sceneSDF(p + vec3(eps, 0, 0)) - sceneSDF(p - vec3(eps, 0, 0)),
          sceneSDF(p + vec3(0, eps, 0)) - sceneSDF(p - vec3(0, eps, 0)),
          sceneSDF(p + vec3(0, 0, eps)) - sceneSDF(p - vec3(0, 0, eps))
        ));
      }

      float ambientOcclusion(vec3 p, vec3 n) {
        if (uIsLowPower > 0.5) {
          float h1 = sceneSDF(p + n * 0.03);
          float h2 = sceneSDF(p + n * 0.06);
          float occ = (0.03 - h1) + (0.06 - h2) * 0.5;
          return clamp(1.0 - occ * 2.0, 0.0, 1.0);
        } else {
          float occ = 0.0;
          float weight = 1.0;
          for (int i = 0; i < 6; i++) {
            float dist = 0.01 + 0.015 * float(i * i);
            float h = sceneSDF(p + n * dist);
            occ += (dist - h) * weight;
            weight *= 0.85;
          }
          return clamp(1.0 - occ, 0.0, 1.0);
        }
      }

      float softShadow(vec3 ro, vec3 rd, float mint, float maxt, float k) {
        if (uIsLowPower > 0.5) {
          float result = 1.0;
          float t = mint;
          for (int i = 0; i < 3; i++) {
            t += 0.3;
            if (t >= maxt) break;
            float h = sceneSDF(ro + rd * t);
            if (h < EPSILON) return 0.0;
            result = min(result, k * h / t);
          }
          return result;
        } else {
          float result = 1.0;
          float t = mint;
          for (int i = 0; i < 20; i++) {
            if (t >= maxt) break;
            float h = sceneSDF(ro + rd * t);
            if (h < EPSILON) return 0.0;
            result = min(result, k * h / t);
            t += h;
          }
          return result;
        }
      }

      float rayMarch(vec3 ro, vec3 rd) {
        float t = 0.0;
        int maxSteps = uIsMobile > 0.5 ? 16 : (uIsSafari > 0.5 ? 16 : 48);

        for (int i = 0; i < 48; i++) {
          if (i >= maxSteps) break;

          vec3 p = ro + rd * t;
          float d = sceneSDF(p);

          if (d < EPSILON) {
            return t;
          }

          if (t > 5.0) {
            break;
          }

          t += d * (uIsLowPower > 0.5 ? 1.2 : 0.9);
        }

        return -1.0;
      }

      vec3 lighting(vec3 p, vec3 rd, float t) {
        if (t < 0.0) {
          return vec3(0.0);
        }

        vec3 normal = calcNormal(p);
        vec3 viewDir = -rd;

        vec3 baseColor = uSphereColor;

        float ao = ambientOcclusion(p, normal);

        vec3 ambient = uLightColor * uAmbientIntensity * ao;

        vec3 lightDir = normalize(uLightPosition);
        float diff = max(dot(normal, lightDir), 0.0);

        float shadow = softShadow(p, lightDir, 0.01, 10.0, 20.0);

        vec3 diffuse = uLightColor * diff * uDiffuseIntensity * shadow;

        vec3 reflectDir = reflect(-lightDir, normal);
        float spec = pow(max(dot(viewDir, reflectDir), 0.0), uSpecularPower);
        float fresnel = pow(1.0 - max(dot(viewDir, normal), 0.0), uFresnelPower);

        vec3 specular = uLightColor * spec * uSpecularIntensity * fresnel;

        vec3 fresnelRim = uLightColor * fresnel * 0.4;

        float distToCursor = length(p - uCursorSphere);
        if (distToCursor < uCursorRadius + 0.4) {
          float highlight = 1.0 - smoothstep(0.0, uCursorRadius + 0.4, distToCursor);
          specular += uLightColor * highlight * 0.2;

          float glow = exp(-distToCursor * 3.0) * 0.15;
          ambient += uLightColor * glow * 0.5;
        }

        vec3 color = (baseColor + ambient + diffuse + specular + fresnelRim) * ao;

        color = pow(color, vec3(uContrast * 0.9));
        color = color / (color + vec3(0.8));

        return color;
      }

      float calculateCursorGlow(vec3 worldPos) {
        float dist = length(worldPos.xy - uCursorSphere.xy);
        float glow = 1.0 - smoothstep(0.0, uCursorGlowRadius, dist);
        glow = pow(glow, 2.0);
        return glow * uCursorGlowIntensity;
      }

      void main() {
        vec2 uv = (gl_FragCoord.xy * 2.0 - uActualResolution.xy) / uActualResolution.xy;
        uv.x *= uResolution.x / uResolution.y;

        vec3 ro = vec3(uv * 2.0, -1.0);
        vec3 rd = vec3(0.0, 0.0, 1.0);

        float t = rayMarch(ro, rd);

        vec3 p = ro + rd * t;

        vec3 color = lighting(p, rd, t);

        float cursorGlow = calculateCursorGlow(ro);
        vec3 glowContribution = uCursorGlowColor * cursorGlow;

        if (t > 0.0) {
          float fogAmount = 1.0 - exp(-t * uFogDensity);
          color = mix(color, uBackgroundColor.rgb, fogAmount * 0.3);

          color += glowContribution * 0.3;

          gl_FragColor = vec4(color, 1.0);
        } else {
          if (cursorGlow > 0.01) {
            gl_FragColor = vec4(glowContribution, cursorGlow * 0.8);
          } else {
            gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
          }
        }
      }
    `,
        transparent: true
      });

      const geometry = new THREE.PlaneGeometry(2, 2);
      mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      // initialize pointer to center
      onPointerMove({
        clientX: window.innerWidth / 2,
        clientY: window.innerHeight / 2
      });

      // event listeners
      window.addEventListener("mousemove", onPointerMove, { passive: true });
      window.addEventListener("touchstart", onTouchStart, { passive: false });
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("touchend", onTouchEnd, { passive: false });
      window.addEventListener("resize", onWindowResize, { passive: true });
      window.addEventListener("orientationchange", () => setTimeout(onWindowResize, 100), {
        passive: true
      });

      // start animation
      animate();
    }

    // ---- event handlers (copied/adapted from your code) ----
    function onTouchStart(e) {
      e.preventDefault();
      if (e.touches && e.touches.length > 0) {
        const t = e.touches[0];
        onPointerMove({ clientX: t.clientX, clientY: t.clientY });
      }
    }
    function onTouchMove(e) {
      e.preventDefault();
      if (e.touches && e.touches.length > 0) {
        const t = e.touches[0];
        onPointerMove({ clientX: t.clientX, clientY: t.clientY });
      }
    }
    function onTouchEnd(e) {
      e.preventDefault();
    }

    function onPointerMove(event) {
      // Normalize coordinates to 0..1 with same orientation you used
      targetMousePosition.x = event.clientX / window.innerWidth;
      targetMousePosition.y = 1.0 - event.clientY / window.innerHeight;

      // compute world position like original
      const normalizedX = targetMousePosition.x;
      const normalizedY = targetMousePosition.y;
      const worldPos = screenToWorldJS(normalizedX, normalizedY);
      cursorSphere3D.copy(worldPos);

      // compute merges & radius exactly like original
      let closestDistance = 1000.0;
      activeMerges = 0;

      const fixedPositions = [
        screenToWorldJS(0.08, 0.85),
        screenToWorldJS(0.25, 0.85),
        screenToWorldJS(0.72, 0.85),
        screenToWorldJS(0.92, 0.85),
      ];

      for (let i = 0; i < fixedPositions.length; i++) {
        const pos = fixedPositions[i];
        const dist = cursorSphere3D.distanceTo(pos);
        closestDistance = Math.min(closestDistance, dist);
        if (dist < settings.mergeDistance) activeMerges++;
      }

      const proximityFactor = Math.max(0, 1.0 - closestDistance / settings.mergeDistance);
      const smoothFactor = proximityFactor * proximityFactor * (3.0 - 2.0 * proximityFactor);
      const dynamicRadius =
        settings.cursorRadiusMin + (settings.cursorRadiusMax - settings.cursorRadiusMin) * smoothFactor;

      // update uniforms for cursor
      if (material && material.uniforms) {
        material.uniforms.uCursorSphere.value.copy(cursorSphere3D);
        material.uniforms.uCursorRadius.value = dynamicRadius;
      }

      updateStory(cursorSphere3D.x, cursorSphere3D.y, dynamicRadius, activeMerges, fps);
    }

    function onWindowResize() {
      if (!renderer || !material) return;
      const width = window.innerWidth;
      const height = window.innerHeight;
      const currentPixelRatio = Math.min(devicePixelRatio, isMobile ? 1.5 : 2);

      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      renderer.setPixelRatio(currentPixelRatio);

      material.uniforms.uResolution.value.set(width, height);
      material.uniforms.uActualResolution.value.set(width * currentPixelRatio, height * currentPixelRatio);
      material.uniforms.uPixelRatio.value = currentPixelRatio;

      // ensure canvas style remains full-screen
      const canvas = renderer.domElement;
      canvas.style.position = "fixed";
      canvas.style.top = "0";
      canvas.style.left = "0";
      canvas.style.width = "100vw";
      canvas.style.height = "100vh";
      canvas.style.zIndex = "0";
      canvas.style.display = "block";

      if (renderer.info) {
        renderer.info.autoReset = true;
      }
    }

    function animate() {
      rafRef.current = requestAnimationFrame(animate);
      render();
    }

    function render() {
      const currentTime = performance.now();
      frameCount++;

      if (currentTime - lastTime >= 1000) {
        fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        updateStory(
          cursorSphere3D.x,
          cursorSphere3D.y,
          material.uniforms.uCursorRadius.value,
          activeMerges,
          fps
        );
        frameCount = 0;
        lastTime = currentTime;
      }

      // Smooth mouse movement (same smoothing)
      mousePosition.x += (targetMousePosition.x - mousePosition.x) * settings.mouseSmoothness;
      mousePosition.y += (targetMousePosition.y - mousePosition.y) * settings.mouseSmoothness;

      material.uniforms.uTime.value = clock.getElapsedTime();
      material.uniforms.uMousePosition.value = mousePosition;

      // small periodic cleanup
      if (performance.now() % 5000 < 16) {
        if (renderer && renderer.renderLists && renderer.renderLists.dispose) {
          try {
            renderer.renderLists.dispose();
          } catch (e) {
            // ignore
          }
        }
      }

      if (renderer && scene && camera) renderer.render(scene, camera);
    }

    // ---- start ----
    init();

    // ---- cleanup on unmount ----
    return () => {
      // cancel animation loop
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      // remove listeners
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("resize", onWindowResize);
      window.removeEventListener("orientationchange", () => setTimeout(onWindowResize, 100));

      // dispose three resources
      try {
        if (material) {
          material.dispose();
        }
        if (mesh) {
          if (mesh.geometry) mesh.geometry.dispose();
          if (mesh.material) mesh.material.dispose();
          scene && scene.remove(mesh);
        }
        if (renderer) {
          const canvas = renderer.domElement;
          if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
          renderer.dispose();
        }
      } catch (e) {
        // swallow
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // container is fixed and full-screen; you can layer other React content over it by z-index
  return (
    <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
      <div 
        ref={containerRef} 
        id="raymarch-container" 
        style={{ 
          position: "relative", 
          inset: 0, 
          zIndex: 0,
        }} 
      />
      {/* Bottom gradient blend overlay */}
      <div 
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "30%",
          background: "linear-gradient(to top, rgba(17, 17, 17, 1) 0%, rgba(17, 17, 17, 0.8) 50%, transparent 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
    </div>
  );
};

export default Home3D;
